/**
 * Reports hub entries — parity with Angular `reports-type.component.html`.
 * `descriptionKey` matches API `report_name` / NgRx store keys for tooltips.
 */
import type { LucideIcon } from 'lucide-react'

export type ReportHubLinkKind = 'react' | 'external' | 'dashboard'

/**
 * Purely presentational grouping for the hub grid (Angular's list has no such
 * grouping — see `reportHubCategories.ts`). Does not affect visibility, policy
 * gating, or navigation.
 */
export type ReportHubCategoryId =
  | 'financial-payments'
  | 'reimbursements'
  | 'property-applications'
  | 'performance-growth'
  | 'communication'
  | 'system-audit-logs'

export interface ReportHubLink {
  label: string
  /** Policy keys passed to `hasPolicy` (any match grants access). */
  policies: string[]
  kind: ReportHubLinkKind
  /** Presentational grouping for the hub grid. */
  category: ReportHubCategoryId
  /** React route under `/reports/…` when `kind === 'react'`. */
  reactPath?: string
  /** Absolute or site-relative href when `kind === 'external'`. */
  externalHref?: string
  /** Dashboard route when `kind === 'dashboard'` (e.g. mailtrap). */
  dashboardPath?: string
  descriptionKey?: string
  /** Requires Liberty org (`org_id === 1`). */
  requiresLibertyOrg?: boolean
  /** Hide when `location.hostname` matches (Angular: cornerstone). */
  hideOnHostnames?: string[]
  /**
   * When true, omit from the All Reports hub and do not register a React route
   * (deeplinks to this report redirect away / 404).
   */
  hidden?: boolean
}

export interface ReportHubCategoryMeta {
  id: ReportHubCategoryId
  label: string
  /** Compact label for the filter tab bar (card header uses `label`). */
  shortLabel: string
  icon: LucideIcon
  /** Selects the icon-badge color variant in ReportsHubPage.module.css. */
  tone: 'green' | 'purple' | 'blue' | 'amber' | 'pink' | 'teal'
}

export interface ReportsHubVisibleGroup {
  category: ReportHubCategoryMeta
  links: ReportHubLink[]
}

export interface ReportPageRouteEntry {
  path: string
  policies: string[]
  label: string
}

export const PROPERTY_DETAIL_LIST_URL =
  'https://reporting.libertyrent.com/listed_properties.php?key=RXaVieOZgP09O'
